import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { Router } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { FeatureSupportService } from 'src/app/services/feature-support.service';
import { AuthService } from 'src/app/services/auth.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { FacilityConfig, Link } from 'src/environments/environment.interface';
import { MobileMenuItem } from 'src/app/models/navbar';
import { DEFAULT_MOBILE_MENU_IDS, resolveMobileMenuItems } from './mobile-menu';
import { Utils } from 'src/app/services/common/utils';
import { LoginComponent } from 'src/app/components/df-login/df-login.component';
import { MemberService } from 'src/app/services/member.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { firstValueFrom, Observable, of, tap } from 'rxjs';
import { MemberDocument, MemberDocumentRequest } from 'src/app/models/document';
import { InterestedPerson } from 'src/app/models/common-models';
import { InterestedPersonService } from 'src/app/services/interested-person.service';
import { DocumentEventType, DocumentIntent } from 'src/app/models/enums';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements AfterViewInit, OnInit {
    @ViewChild('login') loginPopup!: LoginComponent;
    settings: FacilityConfig;
    collapseHeaderExpanded: boolean = true;
    menus: any = {};
    menuTimers: any = {};
    loginTitle: string = "";
    loginDescription: string = "";
    logo = '';
    logoMobile = '';
    navbarLeftLinks: Link[] = [];
    navbarRightLinks: Link[] = [];
    mobileMenuLinks: MobileMenuItem[] = [];
    phoneNumber: string = '';
    interestedPerson: InterestedPerson | null = null;
    memberDocs: MemberDocument[] = [];

    constructor(
        public router: Router,
        private viewportScroller: ViewportScroller,
        private _user: UserService,
        private _memberService: MemberService,
        private _featureSupport: FeatureSupportService,
        private _environmentService: EnvironmentService,
        private _auth: AuthService,
        private localStorageService: LocalStorageService,
        private _interestedPersonService: InterestedPersonService
    ) { }

    public onClick(elementId: string): void { 
        this.classApplied = false;
        this.viewportScroller.scrollToAnchor(elementId);
    }

    ngOnInit(): void {
        this.settings = this._environmentService.getFacilityConfig();
        this.logo = this._environmentService.getLinkUrlById('bigLogo');
        this.logoMobile = this._environmentService.getLinkUrlById('smallLogo') || this.logo;
        this.navbarLeftLinks = this.getOrderedNavbarLeftLinks();
        this.navbarRightLinks = this._environmentService.getLinksByCategory('NavbarRight');
        this.mobileMenuLinks = this.buildMobileMenuLinks();
    }

    /**
     * Returns NavbarLeft links in the order set in config (mobileMenu array).
     * Order is strictly the order of the mobileMenu array; links not in that array are appended in config order.
     */
    private getOrderedNavbarLeftLinks(): Link[] {
        const all = this._environmentService.getLinksByCategory('NavbarLeft');
        const orderIds = this.settings.mobileMenu && this.settings.mobileMenu.length > 0
            ? [...this.settings.mobileMenu]
            : [...DEFAULT_MOBILE_MENU_IDS];
        const byId = new Map(all.map((link) => [link.id, link]));
        const ordered: Link[] = [];
        for (const id of orderIds) {
            const link = byId.get(id);
            if (link?.url) ordered.push(link);
        }
        for (const link of all) {
            if (link.url && !orderIds.includes(link.id)) ordered.push(link);
        }
        return ordered;
    }

    /**
     * Builds the list of links to show in the mobile menu drawer.
     * Order is strictly the order of the mobileMenu array in config (first element = first menu item).
     * Homepage section show flags do not hide nav links — those flags only control homepage tiles.
     */
    private buildMobileMenuLinks(): MobileMenuItem[] {
        return resolveMobileMenuItems(
            this.settings.mobileMenu,
            (id) => this._environmentService.getLinkById(id)
        );
    }

    /** trackBy for mobile menu list so DOM order matches config array order */
    trackByMobileMenuItemId(_index: number, item: MobileMenuItem): string {
        return item.id;
    }

    async ngAfterViewInit() {
        var isLoggedIn = this._auth.isLoggedIn();
        if( isLoggedIn) {
            var registrationCompletedSteps = this._auth.getProp('registrationCompletedSteps');
            if(!registrationCompletedSteps)
            {
                var registration = await this._memberService.getCompeletedRegistrationSteps();
                this._auth.setProp('registrationCompletedSteps', registration.CompletedSteps);
                registrationCompletedSteps = registration.CompletedSteps;
            }
            var user = await this.getCurrentUser();
            var phoneNumber = user?.PhoneNumber || this.localStorageService.getProp('df-auth-key', 'PhoneNumber') || '';
            if (registrationCompletedSteps && !Utils.hasStepCompleted(registrationCompletedSteps, 'profile')) {
                const skipGetStartedProfilePrompt = this.router.url.startsWith('/class-schedule') &&
                    this.localStorageService.getProp('df-auth-key', 'SkipGetStartedProfilePromptOnce');
                if (skipGetStartedProfilePrompt) {
                    this.localStorageService.deleteProp('df-auth-key', 'SkipGetStartedProfilePromptOnce');
                    return;
                }
                setTimeout(() => {
                    this.loginTitle = 'Complete your profile';
                    this.loginDescription = 'Complete your profile to access all features.';
                    this.loginPopup.open(null, phoneNumber, 'register');
                });
                return;
            } else {
                if (!this.router.url.startsWith('/purchase?selectedItemId=')) {
                    this.memberDocs = await this.getAgreementDocuments();
                    if(this.memberDocs.filter(doc => !doc.AgreeStatus).length > 0) {
                        this.loginPopup.open(null, phoneNumber, 'agreement');
                    }
                }
            }
        }
    }

    classApplied = false;
    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    avatar() {
        if (this._auth.isLoggedIn())
            return this._user.getPhotoUrl();
        return null;
    }

    openLoginPopup() {
        this.loginTitle = '';
        this.loginDescription = '';
        this.phoneNumber = this.localStorageService.getProp('df-auth-key', 'PhoneNumber');
        this.loginPopup.open(null, this.phoneNumber, 'phone');
    }

    isLoggedIn() {
        return this._auth.isLoggedIn();
    }

    loginSupported(): boolean {
        return this._featureSupport.isSupported('login');
    }

    logout() {
        this.showMenu({ type: 'mouseout' }, 'acct');
        this._auth.logout();
    }

    showing() {
        this.collapseHeaderExpanded = true;
    }

    collapsing() {
        this.collapseHeaderExpanded = false;
    }

    collapseHeaderClasses() {
        return `side-nav-container mb-2${this.collapseHeaderExpanded?' expanded':' collapsed'}`;
    }

    showMenu(evt: any, menuName: string) {
        if (evt.type == 'mouseover') {
            this.menus[menuName] = 'show';
            if (this.menuTimers[menuName]) {
                clearTimeout(this.menuTimers[menuName]);
                this.menuTimers[menuName] = 0;
            }
        } else if (evt.type == 'mouseout' && this.menus[menuName]) {
            this.menuTimers[menuName] = setTimeout(() => {
                this.menus[menuName] = 'fading';
                setTimeout(() => delete this.menus[menuName], 250);
            }, 2000);
        }
    }

    menuClass(menuName: string) {
        return {
            'show': !!this.menus[menuName],
            'fadeOutMenu': this.menus[menuName] === 'fading'
        }
    }

    loggedIn(isLoggedIn: boolean) {
        if (isLoggedIn)
        {
            window.location.reload();
        }
    }

    getLinkUrlById(id: string): string {
        return this._environmentService.getLinkUrlById(id);
    }

    getLogoLinkUrl(): string {
        return this._environmentService.getLogoLinkUrl();
    }

    getLinkTarget(id: string): string {
        const link = this._environmentService.getLinkById(id);
        return link?.target || '_self';
    }

    /**
     * True when a nav link points at the page currently being viewed, so the tab
     * shows the full "active" underline. Only same-origin, in-app paths qualify —
     * external links, mailto/tel, and the home "/" only light up on their own page.
     */
    isActiveLink(url: string | undefined | null): boolean {
        if (!url) return false;
        if (/^(mailto:|tel:)/i.test(url)) return false;
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        let linkPath: string;
        if (/^https?:\/\//i.test(url)) {
            try {
                const u = new URL(url);
                if (!origin || u.origin !== origin) return false; // external site
                linkPath = u.pathname;
            } catch {
                return false;
            }
        } else {
            linkPath = url.split('#')[0].split('?')[0];
            if (!linkPath.startsWith('/')) linkPath = '/' + linkPath;
        }
        const current = (this.router.url.split('#')[0].split('?')[0]) || '/';
        const norm = (p: string) => (p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p) || '/';
        return norm(current) === norm(linkPath);
    }

    async getAgreementDocuments(): Promise<MemberDocument[]> {
        // Show agreement if needed
        if (!this._auth.isLoggedIn())
            return [];

        var personInfo = await this.getCurrentUser();
        var memberInfo = this._auth.getProp('member');
        if (!personInfo?.PhoneNumber || !memberInfo?.Id)
            return [];

        this.interestedPerson = this._auth.getProp('interestedPerson');
        if(!this.interestedPerson)
        {
            this.interestedPerson = await firstValueFrom(this._interestedPersonService.getInterestedPerson(personInfo.PhoneNumber));    
            this._auth.setProp('interestedPerson', this.interestedPerson);
        }
        // console.log("this.interestedPerson", this.interestedPerson);
        if (this.interestedPerson && this.interestedPerson.MenuPackId && this.interestedPerson.MenuPackId !== 0 && this.interestedPerson.PurchaseId !== '') {
            const req: MemberDocumentRequest = {
                MemberId: memberInfo.Id,
                DocumentEventType: DocumentEventType.Checkout,
                Intent: DocumentIntent.MenuPack,
                IntentId: 0 //return all documents for MenuPack
            };
            var response = await firstValueFrom(this._memberService.getNonAgreedDocuments(req));
            if(response && response.Success && response.Data)
            {
                this.memberDocs = response.Data;
                return this.memberDocs;
            }
        }
        return [];
    }

    private async getCurrentUser(): Promise<any> {
        let user = this._auth.getProp('user');
        if (user)
            return user;

        const user$ = this._user.fetchUser();
        if (!user$)
            return null;

        try {
            user = await firstValueFrom(user$);
            if (user) {
                this._auth.setProp('user', user);
            }
            return user;
        } catch (err) {
            console.error('Unable to fetch logged in user', err);
            return null;
        }
    }
}